/**
 * Discriminator values + shared filter builder for the unified
 * `map-cluster-source` GeoJSON.
 *
 * Tasks, notes and obstruction anchors share a single clustered MapLibre
 * source so the cluster count reflects every clusterable item visible on
 * the map. The `featureKind` property routes each unclustered point to the
 * correct symbol layer (task marker vs. note pin vs. obstruction anchor)
 * and reuses the same `'task' | 'note' | 'obstruction'` taxonomy as the
 * on-map hover preview slot (`HoveredMarkerKind` in `uiSlice`).
 *
 * @module components/map/MapClusterLayer/featureKind
 */

import type {
  ExpressionSpecification,
  FilterSpecification,
} from '@maplibre/maplibre-gl-style-spec';

import type { HoveredMarkerKind } from '@/store/slices/uiSlice';

export const MAP_CLUSTER_FEATURE_KIND_TASK = 'task';
export const MAP_CLUSTER_FEATURE_KIND_NOTE = 'note';
export const MAP_CLUSTER_FEATURE_KIND_OBSTRUCTION = 'obstruction';

/** Re-export of the canonical `'task' | 'note' | 'obstruction'` taxonomy. */
export type MapClusterFeatureKind = HoveredMarkerKind;

/** Shared GeoJSON source ID used by both task and note marker layers. */
export const MAP_CLUSTER_SOURCE_ID = 'map-cluster-source';

/**
 * Builds an "unclustered + matching kind" filter for a layer attached to
 * `map-cluster-source`. Caller can pass extra clauses (e.g. task layers
 * append `['get', 'hasMarker']`).
 */
export function buildUnclusteredFeatureFilter(
  kind: MapClusterFeatureKind,
  ...extra: ExpressionSpecification[]
): FilterSpecification {
  return [
    'all',
    ['!', ['has', 'point_count']],
    ['==', ['get', 'featureKind'], kind],
    ...extra,
  ];
}
